import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { useCachedFetch } from '@/composables/useFetch';
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'
import useMapConfigurationGlobals from '@/composables/useMapConfigurationGlobals'

export let useStormStore = defineStore('stormStore', () => {

  // Storm type codes
  // XX (unknown)
  // LO (low or remnants)
  // SD (subtrop depression)
  // SS (subtrop storm)
  // PT (post-trop)
  // TD (tropical depression)
  // TS (tropical storm)
  // HU (hurricane)

  const {
    $STORM_TRACK_DATA_URL,
    $STORM_TRACK_DATA_FILE,
    $STORM_TRACK_PLOT_DATA_URL,
    $STORM_TRACK_PLOT_DATA_FILE
  } = useConfigurationGlobals();
  const { $STORM_SPAGHETTI_MAP } = useMapConfigurationGlobals();

  const stormRegionBounds = Object.hasOwnProperty.call(window, 'google') && window.google.maps ? {
    "al": new google.maps.LatLngBounds(new google.maps.LatLng(15, -95), new google.maps.LatLng(40, -25)),
    "ep": new google.maps.LatLngBounds(new google.maps.LatLng(10, -160), new google.maps.LatLng(30, -90)),
    "default": new google.maps.LatLngBounds(new google.maps.LatLng(5, -160), new google.maps.LatLng(50, -10))
  } : null;
  
  const stormsData = ref({});
  const plotType = ref('cone') // cone or spaghetti
  const stormAdvisoryText = ref(null);
  const stormForecastContent = ref(null);
  const pointForecastContent = ref(null);

  const selectedStorm = ref(null);
  const visibleStorms = ref(new Set());

  const activeStorms = computed(() => {
    const data = stormsData.value;
    const stormsKeyValues = Object.entries(data);
    const storms = [];
    
    stormsKeyValues.forEach(storm => {
      if (storm[1].active) storms.push({...data[storm[0]]});
    })

    return storms;
  });

  const activeAtlanticStorms = computed(() => activeStorms.value.filter(storm => storm.stormregion === 'al'));
  const activeEasternPacificStorms = computed(() => activeStorms.value.filter(storm => storm.stormregion === 'ep' || storm.stormregion === 'cp'));
  const hasActiveStorms = computed(() => activeStorms.value.length > 0);
  const conePlots = computed(() => activeStorms.value.filter(storm => Object.prototype.hasOwnProperty.call(storm, 'forecast')));
  const spaghettiPlots = computed(() => activeStorms.value.filter(storm => Object.prototype.hasOwnProperty.call(storm, 'spaghetti')));
  const visibleConePlots = computed(() => conePlots.value.filter(storm => visibleStorms.value.has(storm.id)));
  const visibleSpaghettiPlots = computed(() => spaghettiPlots.value.filter(storm => visibleStorms.value.has(storm.id)));

  const stormCategory = computed(() => {
    return (stormId) => {
      const storm = getStorm(stormId);

      if (storm.status.type.toLowerCase() !== 'hu') return false;

      const stormWindSpeed = storm.status.windsustained_kts;

      return stormWindSpeed >= 64 && stormWindSpeed <= 82
        ? 1
        : stormWindSpeed >= 83 && stormWindSpeed <= 95
        ? 2
        : stormWindSpeed >= 96 && stormWindSpeed <= 112
        ? 3
        : stormWindSpeed >= 113 && stormWindSpeed <= 136
        ? 4
        : stormWindSpeed >= 137
        ? 5
        : false;
    }
  })

  async function getStormsData() {
    const url = `${$STORM_TRACK_DATA_URL}${$STORM_TRACK_DATA_FILE}`

    try {
      const { error, data } = await useCachedFetch(url, {}, false)

      if (error.value) {
        throw error.value
      }

      stormsData.value = processStormData(data.value);
      
      const forecastStormData = await buildForecastData(stormsData.value);
      stormsData.value = { ...stormsData.value, ...forecastStormData };
      
      if ($STORM_SPAGHETTI_MAP) {
        await getStormPlotData()
      }
      
      if (activeStorms.value.length === 0) return stormsData.value;
      
      setDefaultSelectedStorm()
      setDefaultvisibleStorms()
      
      return stormsData.value
    } catch (error) {
      console.warn(error)

      return error
    }
  }

  async function getStormKMLContent(fileUrl, apiUrl) {
    if (!fileUrl) return
    
    const fileUrlCopy = `${fileUrl}`;
    const urlParts = fileUrlCopy.split('/')
    const fileName = urlParts[urlParts.length - 1];
    const url = apiUrl ? `${apiUrl}${fileName}` : fileUrl;
    
    try {
      const { error, data } = await useCachedFetch(url, {}, false)
      
      if (error.value) {
        throw error.value
      }
      
      return parseStormKML(data.value);
    } catch (error) {
      console.warn(error)

      return error
    }
  }

  async function getStormAdvisoryText(fileUrl, apiUrl) {
    if (!fileUrl) return

    const urlParts = fileUrl.split('/')
    const fileName = urlParts[urlParts.length - 1]
    const url = `${apiUrl}${fileName}`

    try {
      const { error, data, isLoading } = await useCachedFetch(url, {}, false)

      if (error.value) {
        throw error.value
      }

      return data.value
    } catch (error) {
      console.warn(error)

      return error
    }
  }
  
  async function getStormPlotData() {
    const url = `${$STORM_TRACK_PLOT_DATA_URL}${$STORM_TRACK_PLOT_DATA_FILE}`

    try {
      const { error, data } = await useCachedFetch(url, {}, false)

      if (error.value) {
        throw error.value
      }

      const spaghettiStormData = await buildSpaghettiData(processStormData(data.value));
      
      stormsData.value = { ...stormsData.value, ...spaghettiStormData };
      
      return spaghettiStormData
    } catch (error) {
      console.warn(error)

      return error
    }
  }  

  async function buildSpaghettiData(stormData) {
    const spaghettiData = {};
    const storms = Object.entries(stormData);

    for (const storm of storms) {
      let stormData = storm[1];
      const models = storm[1].models;

      stormData.spaghetti = models;
      delete stormData.models;

      const adeckkmlData = await getStormKMLContent( stormData.spaghetti?.adeckkml, $STORM_TRACK_PLOT_DATA_URL );
      const adeckkmllinesData = await getStormKMLContent( stormData.spaghetti?.adeckkmllines, $STORM_TRACK_PLOT_DATA_URL );
      
      stormData.spaghetti.adeckkmlData = adeckkmlData;
      stormData.spaghetti.adeckkmllinesData = adeckkmllinesData;

      if (Object.hasOwnProperty.call(window, 'google') && window.google.maps) {
        const bounds = Object.hasOwnProperty.call(stormData, 'bounds') ? stormData.bounds : buildStormBounds(adeckkmlData);
        stormData.spaghetti.bounds = bounds;
      }

      if (stormsData.value[storm[0]]) {
        stormData = { ...stormData, ...stormsData.value[storm[0]]};
      }

      spaghettiData[storm[0]] = stormData;
    }

    return spaghettiData
  }

  async function buildForecastData(stormData) {
    if (activeStorms.value.length === 0) return {};
    
    const forecastData = {};
    const storms = Object.entries(stormData);

    for (const storm of storms) {
        let stormData = storm[1];

        const forecastKMLUrl = stormData.forecast?.forecastkml;
        const advisoryTextUrl = stormData.advisory?.advisorytxt;
        const advisorynumber = storm[1].advisory.advisorynumber;
        const advisoryIsIntermediate = advisorynumber.includes('A');
        const advisoryNumberPadCount = advisoryIsIntermediate ? 4 : 3;
        const nhcAdvisoryUrl = `https://stormtrack.weatherflow.com/wwa/${storm[0].toUpperCase()}_${advisorynumber.padStart(advisoryNumberPadCount, '0')}adv_WW.kml`

        const pointForecastContent = await getStormKMLContent(forecastKMLUrl, $STORM_TRACK_DATA_URL);
        const advisoryText = await getStormAdvisoryText(advisoryTextUrl, $STORM_TRACK_DATA_URL);
        const nhcAdvisoryContent = await getStormKMLContent(nhcAdvisoryUrl)

        stormData.forecast.pointForecastContent = pointForecastContent;
        stormData.advisory.advisoryText = advisoryText;
        stormData.advisory.nhcAdvisoryUrl = nhcAdvisoryUrl
        stormData.advisory.nhcAdvisoryContent = nhcAdvisoryContent;

        if (Object.hasOwnProperty.call(window, 'google') && window.google.maps) {
          const bounds = Object.hasOwnProperty.call(stormData, 'bounds') ? stormData.bounds : buildStormBounds(pointForecastContent);
          stormData.forecast.bounds = bounds;
        }

        if (stormsData.value[storm[0]]) {
          stormData = { ...stormsData.value[storm[0]], ...stormData };
          
        }

        forecastData[storm[0]] = stormData;
    }

    return forecastData
  }

  function buildStormBounds(content) {
    if (!content) return;

    const locations = content.querySelectorAll('Placemark > ExtendedData > Data[name="Location"] > value');

    if (!locations || locations.length === 0) return;

    const bounds = new google.maps.LatLngBounds();

    locations.forEach((location) => {
      const coords = location.textContent.trim().split(' ');
      const lat = parseFloat(coords[1]);
      const lng = parseFloat(coords[3]);

      bounds.extend(new google.maps.LatLng(lat, lng));
    });

    return bounds;
  }

  function parseStormKML(data) {
      const parser = new DOMParser();

      return parser.parseFromString(data, 'application/xml')
  }

  function processStormData(data) {
    const jsonText = data.match(/storms_callback\((.*)\)/s)
    const parsedStormData = JSON.parse(jsonText[1])

    const stormIds = Object.keys(parsedStormData)

    stormIds.forEach((stormId) => {
      parsedStormData[stormId].id = stormId;
    })

    return parsedStormData
  }

  function convertPointForecastToObject(stormId, pointId, kmlLayerType) {
    const storm = getStorm(stormId);
    const dataGroup = kmlLayerType.indexOf('spaghetti') > -1 ? 'spaghetti' : 'forecast'
    
    if (!storm) return;

    setSelectedStorm(stormId);

    if (!Object.prototype.hasOwnProperty.call(storm, dataGroup)) return

    const dataTypes = {
      forecast: 'pointForecastContent',
      spaghetti: 'adeckkmlData',
      spaghettiLines: 'adeckkmllinesData',
    }

    const document = storm[dataGroup][dataTypes[kmlLayerType]]

    const primaryDataLabels = ['storm name', 'advisory', 'title', 'valid at', 'model', 'model description'];
    const placemark = document.getElementById(pointId)
    const data = [...placemark.querySelectorAll('Data')];

    const pointForecastDataObject = {};
    pointForecastDataObject.data = {};
    pointForecastDataObject.group = dataGroup;

    data.forEach(d => {
      const name = d.getAttribute('name');
      const key = name.toLowerCase().split(' ').join('-');
      const value = d.querySelector('value').innerHTML;

      if (primaryDataLabels.includes(name.toLowerCase())) {
        pointForecastDataObject[key] = value

        return;
      }
      
      pointForecastDataObject.data[key] = {
        label: name,
        value
      }
    })

    pointForecastContent.value = pointForecastDataObject;
  }

  function convertNHCAdviosryToObject(stormId, featureData) {
    const storm = getStorm(stormId);
    
    if (!storm) return;

    setSelectedStorm(stormId);

    const infoWindowContent = document.createElement('div');
    infoWindowContent.innerHTML = featureData.info_window_html;

    const dataElements = [...infoWindowContent.querySelectorAll('tr')];
    const dataKeyValueElements = dataElements.map(dataElement => [...dataElement.querySelectorAll('td')]);

    const primaryDataLabels = ['storm name', 'advisory', 'title', 'valid at', 'model', 'model description'];
    const formattedDataLabels = {
      advisoryDate: 'Advisory Date',
    }
    const excludeDataLabels = [
      'timezone',
      'stormType',
      'basin',
      'fcstpd',
      'storm',
      'atcfid',
      'advisoryNum',
      'stormNum',
      'stormName'
    ]

    const pointForecastDataObject = {};
    pointForecastDataObject.data = {};
    pointForecastDataObject.group = 'forecast';

    dataKeyValueElements.forEach((data) => {
      const key = data[0].innerText.trim()
      const value = data[1].innerText.trim()

      if (excludeDataLabels.includes(key)) return;

      if (primaryDataLabels.includes(key.toLowerCase())) {
        pointForecastDataObject[key] = value

        return
      }

      pointForecastDataObject.data[key] = {
        label: formattedDataLabels[key] || key,
        value
      }
    })

    pointForecastDataObject.title = featureData.name
    pointForecastContent.value = pointForecastDataObject;
  }

  function setDefaultSelectedStorm() {
    if (activeStorms.value.length < 0) return

    const defaultSelectedStorm = activeStorms.value.find((storm) => {
      const kmlLayerType = plotType.value === 'cone' ? 'forecast' : 'spaghetti';

      if (Object.prototype.hasOwnProperty.call(storm, kmlLayerType)) return storm;

      return null;
    });

    if (!defaultSelectedStorm) {
      plotType.value = plotType.value === 'cone' ? 'spaghetti' : 'cone';
      setDefaultSelectedStorm();
      return;
    };

    selectedStorm.value = defaultSelectedStorm;
  }

  function setDefaultvisibleStorms() {
    if (activeStorms.value.length < 0) return;

    return activeStorms.value.forEach(storm => {
      visibleStorms.value.add(storm.id);
    });
  }

  function setSelectedStorm(stormId) {
    selectedStorm.value = getStorm(stormId);

    getStormKMLContent();
    getStormAdvisoryText();

    return selectedStorm.value;
  }

  function checkSelectedStorm() {
    if (!selectedStorm.value) return;

    const kmlLayerType = plotType.value === 'cone' ? 'forecast' : 'spaghetti';

    if (!Object.prototype.hasOwnProperty.call(selectedStorm.value, kmlLayerType)) setDefaultSelectedStorm();
  }

  function isStormSelected(stormId) {
    return visibleStorms.value.has(stormId);
  }

  function setPlotType(type) {
    plotType.value = type;
  }

  function togglePlotType() {
    plotType.value = plotType.value === 'cone' ? 'spaghetti' : 'cone';
  }

  function hasPlotType(stormId = null, type) {
    const storm = stormId ? selectedStorm.value : getStorm(stormId)
    const plotTypeKey = type === 'cone' ? 'forecast' : 'spaghetti'

    return Object.prototype.hasOwnProperty.call(storm, plotTypeKey)
  }

  function getStorm(stormId) {
    return activeStorms.value.filter(storm => storm.id === stormId)[0];
  }

  function updatePointForecastContent(content) {
    pointForecastContent.value = content;
  }

  const stormForecastURL = (stormId = selectedStorm.value.id) => getStorm(stormId)?.forecast?.forecastkml;
  const stormAdvisoryURL = (stormId = selectedStorm.value.id) => getStorm(stormId)?.advisory?.advisorytxt;
  const stormSpaghtettiLinesURL = (stormId = selectedStorm.value.id) => getStorm(stormId)?.spaghetti?.adeckkmllines;
  const stormSpaghtettiURL = (stormId = selectedStorm.value.id) => getStorm(stormId)?.spaghetti?.adeckkml;

  return {
    activeAtlanticStorms,
    activeEasternPacificStorms,
    activeStorms,
    conePlots,
    hasActiveStorms,
    plotType,
    pointForecastContent,
    selectedStorm,
    visibleStorms,
    visibleConePlots,
    visibleSpaghettiPlots,
    spaghettiPlots,
    stormAdvisoryText,
    stormCategory,
    stormForecastContent,
    stormRegionBounds,
    stormsData,
    convertNHCAdviosryToObject,
    convertPointForecastToObject,
    checkSelectedStorm,
    getStorm,
    getStormAdvisoryText,
    getStormPlotData,
    getStormKMLContent,
    getStormsData,
    hasPlotType,
    isStormSelected,
    setPlotType,
    setSelectedStorm,
    stormAdvisoryURL,
    stormForecastURL,
    stormSpaghtettiURL,
    stormSpaghtettiLinesURL,
    togglePlotType,
    updatePointForecastContent
  }
})